import React, { useContext, useState, useEffect, useRef } from 'react';
import { DateTime } from 'luxon';
import { AdapterLuxon } from '@mui/x-date-pickers/AdapterLuxon';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { TableContext } from '../context';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { McIcon } from "@maersk-global/mds-react-wrapper/components-core/mc-icon";
import { McButton } from "@maersk-global/mds-react-wrapper/components-core/mc-button";
import { McModal } from "@maersk-global/mds-react-wrapper/components-core/mc-modal";
import { McRadioGroup } from "@maersk-global/mds-react-wrapper/components-core/mc-radio-group";
import { McRadio } from "@maersk-global/mds-react-wrapper/components-core/mc-radio";
import axios from 'axios';
import { toast } from 'react-toastify';

export default function Time() {
    const { selectedTime, setSelectedTime, setLoading, graphData, userPreferences, setUserPreferences, applyTimePreference } = useContext(TableContext);
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const [startDate, setStartDate] = useState(DateTime.now());
    const [endDate, setEndDate] = useState(DateTime.now());
    const [temporaryDatePickerTime, setTemporaryDatePickerTime] = useState({start: DateTime.now(), end: DateTime.now()});
    const [temporaryTimeOptionTime, setTemporaryTimeOptionTime] = useState({start: DateTime.now(), end: DateTime.now()});
    const [showSetButton, setShowSetButton] = useState(false);
    const [lastChanged, setLastChanged] = useState('');
    const [isPreferencesModalOpen, setIsPreferencesModalOpen] = useState(false);
    const [selectedPreference, setSelectedPreference] = useState('');
    const [isPreferencesSaving, setIsPreferencesSaving] = useState(false);

    const darkPrimaryTheme = createTheme({
        palette: {
            primary: {
            main: '#141414',
            },
        },
    });

    useEffect(() => {
        console.log('temporaryDatePickerTime:', temporaryDatePickerTime);
        console.log('temporaryTimeOptionTime:', temporaryTimeOptionTime);
        console.log('lastChanged:', lastChanged);
    }, [temporaryDatePickerTime, temporaryTimeOptionTime, lastChanged]);

    useEffect(() => {
        // Load saved preference on component mount
        const savedPreference = localStorage.getItem('defaultTimePreference');
        if (savedPreference) {
            setSelectedPreference(savedPreference);
        } else if (userPreferences?.changes?.duration) {
            // Use preference from context if available
            setSelectedPreference(userPreferences.changes.duration);
        }
    }, [userPreferences]);

    // Update temporary time variables when selectedTime changes from context
    useEffect(() => {
        if (selectedTime && selectedTime.start && selectedTime.end) {
            const startISO = DateTime.fromJSDate(selectedTime.start).toISO();
            const endISO = DateTime.fromJSDate(selectedTime.end).toISO();
            
            // Update temporary time option time to reflect the current selectedTime
            setTemporaryTimeOptionTime({ start: startISO, end: endISO });
            setLastChanged('timeOption');
        }
    }, [selectedTime]);

    const handleDateChange = () => {
        const utcStartDate = startDate.toJSDate();
        const utcEndDate = endDate.toJSDate();
        setTemporaryDatePickerTime({ start: startDate.toISO(), end: endDate.toISO() });
        setSelectedTime({ start: utcStartDate, end: utcEndDate });
        setLoading(true);
        setIsDropdownOpen(false);
        setShowSetButton(false);
        setLastChanged('datePicker');
    };

    const handleTimeOptionClick = (start, end) => {
        const utcStartDate = DateTime.fromISO(start, { zone: 'UTC' }).toJSDate();
        const utcEndDate = DateTime.fromISO(end, { zone: 'UTC' }).toJSDate();
        setTemporaryTimeOptionTime({ start, end });
        setSelectedTime({ start: utcStartDate, end: utcEndDate });
        setLoading(true);
        setIsDropdownOpen(false);
        setLastChanged('timeOption');
    };

    const handlePreferencesClick = () => {
        setIsPreferencesModalOpen(true);
    };

    const handlePreferencesModalClose = () => {
        setIsPreferencesModalOpen(false);
        setSelectedPreference('');
    };

    const handlePreferenceSelection = (e) => {
        setSelectedPreference(e.detail);
    };

    const handlePreferencesSave = async () => {
        if (!selectedPreference) {
            toast.error("Please select a time preference", {
                position: "top-center",
                autoClose: 3000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
                progress: undefined,
                theme: "light"
            });
            return;
        }

        setIsPreferencesSaving(true);

        try {
            // 1. Make API call to store user preference
            const response = await axios.post(
                `${process.env.REACT_APP_BACKEND_URI}/api/auth/store_user_preference`,
                {
                    userEmail: graphData?.mail,
                    preference: {
                        changes: {
                            duration: selectedPreference
                        }
                    }
                },
                {
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: "Bearer " + localStorage.getItem("jwt_token"),
                    }
                }
            );

            if (response.data.status === 200) {
                // 2. Set the time to the selected option
                const selectedOption = timeOptions.find(option => option.label === selectedPreference);
                if (selectedOption) {
                    const utcStartDate = DateTime.fromISO(selectedOption.start, { zone: 'UTC' }).toJSDate();
                    const utcEndDate = DateTime.fromISO(selectedOption.end, { zone: 'UTC' }).toJSDate();
                    setTemporaryTimeOptionTime({ start: selectedOption.start, end: selectedOption.end });
                    setSelectedTime({ start: utcStartDate, end: utcEndDate });
                    setLoading(true);
                    setLastChanged('timeOption');
                }

                // 3. Update context preferences
                setUserPreferences(prevPrefs => ({
                    ...prevPrefs,
                    changes: {
                        ...prevPrefs.changes,
                        duration: selectedPreference
                    }
                }));

                toast.success("Time preference saved successfully", {
                    position: "top-center",
                    autoClose: 3000,
                    hideProgressBar: false,
                    closeOnClick: true,
                    pauseOnHover: true,
                    draggable: true,
                    progress: undefined,
                    theme: "light"
                });

                setIsPreferencesModalOpen(false);
                setSelectedPreference('');
                setIsDropdownOpen(false);
            } else {
                throw new Error(response.data.message || "Failed to save preference");
            }
        } catch (error) {
            console.error("Error saving time preference:", error);
            toast.error("Failed to save time preference", {
                position: "top-center",
                autoClose: 3000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
                progress: undefined,
                theme: "light"
            });
        } finally {
            setIsPreferencesSaving(false);
        }
    };

    const timeOptions = [
        { label: "Current Time", start: DateTime.now().toISO(), end: DateTime.now().toISO() },
        { label: "Last 5 mins", start: DateTime.now().minus({ minutes: 5 }).toISO(), end: DateTime.now().toISO() },
        { label: "Last 10 mins", start: DateTime.now().minus({ minutes: 10 }).toISO(), end: DateTime.now().toISO() },
        { label: "Last 30 mins", start: DateTime.now().minus({ minutes: 30 }).toISO(), end: DateTime.now().toISO() },
        { label: "Last hour", start: DateTime.now().minus({ hours: 1 }).toISO(), end: DateTime.now().toISO() },
        { label: "Last 2 hours", start: DateTime.now().minus({ hours: 2 }).toISO(), end: DateTime.now().toISO() },
        { label: "Last 3 hours", start: DateTime.now().minus({ hours: 3 }).toISO(), end: DateTime.now().toISO() },
        { label: "Last 4 hours", start: DateTime.now().minus({ hours: 4 }).toISO(), end: DateTime.now().toISO() },
        { label: "Last 6 hours", start: DateTime.now().minus({ hours: 6 }).toISO(), end: DateTime.now().toISO() },
        { label: "Last 12 hours", start: DateTime.now().minus({ hours: 12 }).toISO(), end: DateTime.now().toISO() },
        { label: "Last day", start: DateTime.now().minus({ days: 1 }).toISO(), end: DateTime.now().toISO() },
        { label: "Last 2 days", start: DateTime.now().minus({ days: 2 }).toISO(), end: DateTime.now().toISO() },
        { label: "Last 3 days", start: DateTime.now().minus({ days: 3 }).toISO(), end: DateTime.now().toISO() },
        { label: "Last 4 days", start: DateTime.now().minus({ days: 4 }).toISO(), end: DateTime.now().toISO() },
        { label: "Last 5 days", start: DateTime.now().minus({ days: 5 }).toISO(), end: DateTime.now().toISO() },
        { label: "Last week", start: DateTime.now().minus({ weeks: 1 }).toISO(), end: DateTime.now().toISO() },
        { label: "Last 2 weeks", start: DateTime.now().minus({ weeks: 2 }).toISO(), end: DateTime.now().toISO() },
        { label: "Last month", start: DateTime.now().minus({ months: 1 }).toISO(), end: DateTime.now().toISO() },
        { label: "Upcoming 5 mins", start: DateTime.now().toISO(), end: DateTime.now().plus({ minutes: 5 }).toISO() },
        { label: "Upcoming 10 mins", start: DateTime.now().toISO(), end: DateTime.now().plus({ minutes: 10 }).toISO() },
        { label: "Upcoming 30 mins", start: DateTime.now().toISO(), end: DateTime.now().plus({ minutes: 30 }).toISO() },
        { label: "Upcoming hour", start: DateTime.now().toISO(), end: DateTime.now().plus({ hours: 1 }).toISO() },
        { label: "Upcoming 2 hours", start: DateTime.now().toISO(), end: DateTime.now().plus({ hours: 2 }).toISO() },
        { label: "Upcoming 3 hours", start: DateTime.now().toISO(), end: DateTime.now().plus({ hours: 3 }).toISO() },
        { label: "Upcoming 4 hours", start: DateTime.now().toISO(), end: DateTime.now().plus({ hours: 4 }).toISO() },
        { label: "Upcoming 6 hours", start: DateTime.now().toISO(), end: DateTime.now().plus({ hours: 6 }).toISO() },
        { label: "Upcoming 12 hours", start: DateTime.now().toISO(), end: DateTime.now().plus({ hours: 12 }).toISO() },
        { label: "Upcoming day", start: DateTime.now().toISO(), end: DateTime.now().plus({ days: 1 }).toISO() },
        { label: "Upcoming 2 days", start: DateTime.now().toISO(), end: DateTime.now().plus({ days: 2 }).toISO() },
        { label: "Upcoming 3 days", start: DateTime.now().toISO(), end: DateTime.now().plus({ days: 3 }).toISO() },
        { label: "Upcoming 4 days", start: DateTime.now().toISO(), end: DateTime.now().plus({ days: 4 }).toISO() },
        { label: "Upcoming 5 days", start: DateTime.now().toISO(), end: DateTime.now().plus({ days: 5 }).toISO() },
        { label: "Upcoming week", start: DateTime.now().toISO(), end: DateTime.now().plus({ weeks: 1 }).toISO() },
        { label: "Upcoming 2 weeks", start: DateTime.now().toISO(), end: DateTime.now().plus({ weeks: 2 }).toISO() },
        { label: "Upcoming month", start: DateTime.now().toISO(), end: DateTime.now().plus({ months: 1 }).toISO() },
    ];

    const displayTime = lastChanged === 'datePicker' ? temporaryDatePickerTime : temporaryTimeOptionTime;

    return (
        <div style={{ position: 'relative', width: 'fit-content' }}>
            <div 
                style={{ 
                    width: '28vw', 
                    height: "fit-content", 
                    display: 'flex', 
                    alignItems: 'center', 
                    cursor: 'pointer', 
                    justifyContent: 'space-between', 
                    border: '1px solid black',
                    padding: '10px',
                    borderRadius: '5px',
                    gap: '5px' 
                }}
                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
            >
                <span style={{fontSize: "0.83rem"}}> Time: {DateTime.fromISO(displayTime.start, {zone: "UTC"}).toFormat('dd-MM-yyyy HH:mm:ss')} UTC - {DateTime.fromISO(displayTime.end, {zone: "UTC"}).toFormat('dd-MM-yyyy HH:mm:ss')} UTC</span>
                <McIcon
                    icon="chevron-down"
                    style={{ 
                        transform: isDropdownOpen ? 'rotate(180deg)' : 'rotate(0deg)', 
                        transition: 'transform 0.3s ease',
                        color: '#141414' 
                    }}
                />
            </div>

            {isDropdownOpen && (
                <div style={{
                    display: 'flex',
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    border: '1px solid #ccc',
                    backgroundColor: 'white',
                    zIndex: 1000,
                    padding: '10px',
                    boxShadow: '0px 4px 6px rgba(0, 0, 0, 0.1)',
                    width: "100%",
                    justifyContent: 'space-between',
                    height: '28vh',
                    flexDirection: 'column',
                    overflowY: 'auto',
                    borderRadius: '5px',
                }}>
                    <div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', height: '100%' }}>
                        <div style={{ marginRight: '10px', padding: '5px', width: "60%", display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                            <ThemeProvider theme={darkPrimaryTheme}>
                                <LocalizationProvider dateAdapter={AdapterLuxon}>
                                    <>
                                        <div style={{ marginBottom: '10px' }}>
                                            <label style={{fontSize: '0.8rem'}}>Start Date</label>
                                            <DateTimePicker
                                                ampm={false}
                                                value={startDate.setZone('UTC')}
                                                onChange={(date) => {
                                                    if (date) {
                                                        setStartDate(date);
                                                        setShowSetButton(date && endDate);
                                                    }
                                                }}
                                                textField={(params) => (
                                                    <input 
                                                        {...params} 
                                                        value={startDate ? startDate.toFormat('dd-MM-yyyy HH:mm:ss') : ''}
                                                    />
                                                )}
                                            />
                                        </div>
                                        <div>
                                            <label style={{fontSize: '0.8rem'}}>End Date</label>
                                            <DateTimePicker
                                                ampm={false}
                                                value={endDate.setZone('UTC')}
                                                onChange={(date) => {
                                                    if (date) {
                                                        setEndDate(date);
                                                        setShowSetButton(startDate && date);
                                                    }
                                                }}
                                                minDateTime={startDate}
                                                // maxDateTime={startDate.plus({ days: 30 })}
                                                textField={(params) => (
                                                    <input 
                                                        {...params} 
                                                        value={endDate ? endDate.toFormat('dd-MM-yyyy HH:mm:ss') : ''}
                                                    />
                                                )}
                                            />
                                        </div>
                                    </>
                                </LocalizationProvider>
                            </ThemeProvider>
                            {showSetButton && (
                                <McButton
                                    label="Set Time"
                                    appearance="primary"
                                    fit="small"
                                    click={handleDateChange}
                                    style={{ marginTop: '10px' }}
                                />
                            )}
                            <div style={{ height: '20px', opacity: '0' }}>" "</div>
                        </div>

                        <div style={{ width: '1px', backgroundColor: '#ddd', margin: '10px' }}></div>

                        <div style={{ display: 'flex', flexDirection: 'column', width: "40%", height: '100%' }}>
                            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '10px', flexShrink: 0 }}>
                                <McButton
                                    label="Set Preference"
                                    icon="cog"
                                    appearance="primary"
                                    fit="small"
                                    variant="outlined"
                                    click={handlePreferencesClick}
                                />
                            </div>
                            <div style={{ display: 'flex', flexDirection: 'column', overflowY: 'auto', flex: 1 }}>
                                {timeOptions.map((option, index) => (
                                    <div 
                                        key={index}
                                        style={{ padding: '5px', cursor: 'pointer', borderBottom: '1px solid #ddd' }}
                                        onClick={() => handleTimeOptionClick(option.start, option.end)}
                                    >
                                        {option.label}
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            <McModal
                heading="Default Time Window Preference"
                body=""
                fit="small"
                open={isPreferencesModalOpen}
                closed={handlePreferencesModalClose}
                closing={handlePreferencesModalClose}
            >
                <div
                    style={{
                        display: "flex",
                        flexDirection: "column",
                        paddingTop: "10px",
                        gap: "16px"
                    }}
                >
                    <p style={{
                        fontSize: '14px',
                    }}>
                        This will be the default time window in which changes appear when you login to Flux portal.
                    </p>
                    <div style={{
                        padding: '0px 15px 15px'
                    }}>
                        <style>
                            {`
                                .time-preference-radio-group::part(fieldset-container) {
                                    display: grid;
                                    grid-template-columns: 1fr 1fr;
                                    column-gap: 2rem;
                                }
                            `}
                        </style>
                        <McRadioGroup
                            className="time-preference-radio-group"
                            legend=""
                            name="timePreference"
                            change={handlePreferenceSelection}
                        >
                            {timeOptions.map((option, index) => (
                                <McRadio
                                    key={index}
                                    name="timePreference"
                                    value={option.label}
                                    label={option.label}
                                    checked={selectedPreference === option.label}
                                />
                            ))}
                        </McRadioGroup>
                    </div>
                </div>
                    <McButton 
                        slot="primaryAction" 
                        appearance="primary" 
                        fit="small" 
                        // dialogaction="ok"
                        loading={isPreferencesSaving}
                        disabled={!selectedPreference || isPreferencesSaving}
                        click={handlePreferencesSave}
                    >
                        Save
                    </McButton>
                    <McButton 
                        slot="secondaryAction" 
                        appearance="neutral" 
                        variant="filled" 
                        dialogaction="cancel" 
                        fit="small"
                        click={handlePreferencesModalClose}
                    >
                        Cancel
                    </McButton>
                </McModal>
        </div>
    );
}